import React, { useState, useEffect } from "react";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import axios from "axios";
import * as localforage from "localforage";
import Swal from "sweetalert2";
import { Link, useNavigate } from "react-router-dom";

// Types
interface UnifiedDocument {
  id: string;
  display_name: string;
  file_url: string;
  formatted_file_size: string;
  file_extension: string;
  is_image: boolean;
  is_video: boolean;
  is_audio: boolean;
  is_document: boolean;
  origin_type: 'managed' | 'collaboration' | 'ehr' | 'chat' | 'service_request';
  origin_id: string;
  origin_label: string;
  created_at: string;
}

interface UnifiedDocumentManagerProps {
  isPickerMode?: boolean;
  onFileSelect?: (file: UnifiedDocument) => void;
  onClose?: () => void;
}

const UnifiedDocumentManager: React.FC<UnifiedDocumentManagerProps> = ({ 
  isPickerMode = false, 
  onFileSelect,
  onClose 
}) => {
  const [documents, setDocuments] = useState<UnifiedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [originFilter, setOriginFilter] = useState('all');
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const baseUrl = `${process.env.REACT_APP_API_URL || 'http://64.227.174.0:8004'}/filemanager/api/v1`;

  const getAuthHeaders = async () => {
    const clinicId = await localforage.getItem('clinic_id') as string;
    const staffId = await localforage.getItem('staff_id') as string;
    const token = await localforage.getItem('token') as string;
    
    return {
      'X-Clinic-ID': clinicId || '',
      'X-Staff-ID': staffId || '',
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    loadDocuments();
  }, [originFilter]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      // Using search param if it exists, otherwise just filter by origin
      const params = new URLSearchParams();
      if (originFilter !== 'all') params.append('origin_type', originFilter);
      if (searchQuery) params.append('search', searchQuery);

      const res = await axios.get(`${baseUrl}/unified-documents/?${params.toString()}`, { headers });
      setDocuments(res.data.results || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading documents:', error);
      setLoading(false);
      if (!isPickerMode) {
        Swal.fire('Notice', 'Could not load documents at this time', 'warning');
      }
    }
  };

  // Trigger search when user presses Enter or after a small delay
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadDocuments();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const getFileIcon = (doc: UnifiedDocument) => {
    if (doc.is_image) return 'bx-image';
    if (doc.is_video) return 'bx-video';
    if (doc.is_audio) return 'bx-music';
    if (doc.is_document) {
      if (doc.file_extension === '.pdf') return 'bxs-file-pdf';
      return 'bx-file-doc';
    }
    return 'bx-file';
  };

  const getFileColor = (doc: UnifiedDocument) => {
    if (doc.is_image) return 'text-success';
    if (doc.is_video) return 'text-warning';
    if (doc.is_audio) return 'text-info';
    if (doc.file_extension === '.pdf') return 'text-danger';
    if (doc.is_document) return 'text-primary';
    return 'text-secondary';
  };

  const getOriginIcon = (originType: string) => {
    switch (originType) {
      case 'managed': return 'bx-folder';
      case 'collaboration': return 'bx-network-chart';
      case 'ehr': return 'bx-user-circle';
      case 'chat': return 'bx-message-square-dots';
      case 'service_request': return 'bx-git-pull-request';
      default: return 'bx-file';
    }
  };

  const handleDownload = (doc: UnifiedDocument) => {
    if (doc.file_url) {
      const link = document.createElement('a');
      link.href = doc.file_url.startsWith('http') ? doc.file_url : `http://64.227.174.0:8004${doc.file_url}`;
      link.setAttribute('download', doc.display_name);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      Swal.fire('Error', 'File URL is not available', 'error');
    }
  };

  const navigateToOrigin = (doc: UnifiedDocument) => {
    switch (doc.origin_type) {
      case 'collaboration':
        navigate(`/institution/collaboration-details?id=${doc.origin_id}`);
        break;
      case 'ehr':
        navigate(`/institution/patient-ehr/${doc.origin_id}`); // Assuming origin_id is patient or medical record id
        break;
      // add more navigation logic as needed
      default:
        Swal.fire('Info', `This file originated from ${doc.origin_label}`, 'info');
    }
  };

  const contentRenderer = () => (
    <div className="container-fluid py-4">
      {/* Header */}
      {!isPickerMode && (
        <div className="row mb-4 align-items-center">
          <div className="col-12 d-flex justify-content-between">
            <div className="d-flex align-items-center">
              <button className="btn btn-link p-0 me-3 text-dark" onClick={() => setShowSidebar(!showSidebar)}>
                <i className="bx bx-menu fs-4"></i>
              </button>
              <h4 className="mb-0 fw-bold" style={{ color: '#2c3e50' }}>Unified Document Manager</h4>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        {/* Sidebar Filters */}
        {showSidebar && (
          <div className="col-xl-3 col-lg-4 mb-4">
            <div className="card shadow-sm border-0" style={{ borderRadius: '15px' }}>
              <div className="card-body">
                <h6 className="card-title fw-bold mb-3 text-muted text-uppercase" style={{ fontSize: '0.85rem', letterSpacing: '1px' }}>
                  Filter by Origin
                </h6>
                <div className="list-group list-group-flush">
                  <button 
                    className={`list-group-item list-group-item-action d-flex align-items-center border-0 ${originFilter === 'all' ? 'active bg-primary text-white rounded' : ''}`}
                    onClick={() => setOriginFilter('all')}
                  >
                    <i className="bx bx-grid-alt me-3 fs-5"></i> All Files
                  </button>
                  <button 
                    className={`list-group-item list-group-item-action d-flex align-items-center border-0 ${originFilter === 'managed' ? 'active bg-primary text-white rounded' : ''}`}
                    onClick={() => setOriginFilter('managed')}
                  >
                    <i className="bx bx-folder me-3 fs-5"></i> My Uploads
                  </button>
                  <button 
                    className={`list-group-item list-group-item-action d-flex align-items-center border-0 ${originFilter === 'collaboration' ? 'active bg-primary text-white rounded' : ''}`}
                    onClick={() => setOriginFilter('collaboration')}
                  >
                    <i className="bx bx-network-chart me-3 fs-5"></i> Collaborations
                  </button>
                  <button 
                    className={`list-group-item list-group-item-action d-flex align-items-center border-0 ${originFilter === 'ehr' ? 'active bg-primary text-white rounded' : ''}`}
                    onClick={() => setOriginFilter('ehr')}
                  >
                    <i className="bx bx-user-circle me-3 fs-5"></i> EHR Documents
                  </button>
                  <button 
                    className={`list-group-item list-group-item-action d-flex align-items-center border-0 ${originFilter === 'chat' ? 'active bg-primary text-white rounded' : ''}`}
                    onClick={() => setOriginFilter('chat')}
                  >
                    <i className="bx bx-message-square-dots me-3 fs-5"></i> Chat Attachments
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className={showSidebar ? 'col-xl-9 col-lg-8' : 'col-12'}>
          {/* Toolbar */}
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '15px' }}>
            <div className="card-body py-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="search-box position-relative" style={{ maxWidth: '400px', width: '100%' }}>
                <input 
                  type="text" 
                  className="form-control rounded-pill bg-light border-0 ps-5" 
                  placeholder="Search files..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <i className="bx bx-search position-absolute top-50 translate-middle-y fs-5 text-muted" style={{ left: '15px' }}></i>
              </div>
              
              <div className="d-flex align-items-center gap-2">
                <div className="btn-group bg-light rounded-pill p-1">
                  <button 
                    className={`btn btn-sm rounded-pill ${viewMode === 'grid' ? 'btn-white shadow-sm fw-bold' : 'btn-light text-muted border-0'}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <i className="bx bx-grid-alt fs-5"></i>
                  </button>
                  <button 
                    className={`btn btn-sm rounded-pill ${viewMode === 'list' ? 'btn-white shadow-sm fw-bold' : 'btn-light text-muted border-0'}`}
                    onClick={() => setViewMode('list')}
                  >
                    <i className="bx bx-list-ul fs-5"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Files */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="mt-2 text-muted">Loading documents...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="card shadow-sm border-0 text-center py-5" style={{ borderRadius: '15px' }}>
              <div className="card-body">
                <i className="bx bx-folder-open font-size-48 text-muted mb-3" style={{ fontSize: '4rem' }}></i>
                <h5 className="fw-bold">No documents found</h5>
                <p className="text-muted">Try adjusting your filters or search query.</p>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="row g-3">
              {documents.map((doc) => (
                <div key={doc.id} className="col-xl-3 col-lg-4 col-md-6">
                  <div className="card shadow-sm border-0 h-100 doc-card" style={{ borderRadius: '15px', transition: 'transform 0.2s, box-shadow 0.2s' }}>
                    <div className="card-body p-3 d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="badge bg-light text-dark rounded-pill border">
                          <i className={`${getOriginIcon(doc.origin_type)} me-1`}></i>
                          {doc.origin_type}
                        </span>
                        {isPickerMode ? (
                          <button 
                            className="btn btn-sm btn-primary rounded-pill px-3"
                            onClick={() => onFileSelect && onFileSelect(doc)}
                          >
                            Select
                          </button>
                        ) : (
                          <div className="dropdown">
                            <button className="btn btn-link text-muted p-0" data-bs-toggle="dropdown">
                              <i className="bx bx-dots-vertical-rounded fs-5"></i>
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0" style={{ borderRadius: '10px' }}>
                              <li><button className="dropdown-item" onClick={() => handleDownload(doc)}><i className="bx bx-download me-2"></i>Download</button></li>
                              <li><button className="dropdown-item" onClick={() => navigateToOrigin(doc)}><i className="bx bx-link-external me-2"></i>Go to Origin</button></li>
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-center my-3 flex-grow-1 d-flex flex-column justify-content-center">
                        <i className={`bx ${getFileIcon(doc)} ${getFileColor(doc)}`} style={{ fontSize: '4rem' }}></i>
                        <h6 className="mt-3 mb-1 text-truncate fw-bold" title={doc.display_name}>{doc.display_name}</h6>
                        <small className="text-muted">{doc.formatted_file_size}</small>
                      </div>
                      
                      <div className="mt-auto border-top pt-2">
                        <small className="text-muted text-truncate d-block" title={doc.origin_label}>
                          <i className="bx bx-info-circle me-1"></i> {doc.origin_label}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card shadow-sm border-0" style={{ borderRadius: '15px', overflow: 'hidden' }}>
              <div className="table-responsive">
                <table className="table table-hover table-nowrap mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th className="border-0">Name</th>
                      <th className="border-0">Origin</th>
                      <th className="border-0">Size</th>
                      <th className="border-0">Date</th>
                      <th className="border-0 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className={`avatar-sm me-3 d-flex align-items-center justify-content-center rounded bg-light ${getFileColor(doc)}`}>
                              <i className={`bx ${getFileIcon(doc)} fs-4`}></i>
                            </div>
                            <div>
                              <h6 className="mb-0 text-truncate fw-bold" style={{ maxWidth: '250px' }} title={doc.display_name}>
                                {doc.display_name}
                              </h6>
                              <small className="text-muted">{doc.file_extension}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: '200px' }} title={doc.origin_label}>
                            <span className="badge bg-light text-dark rounded-pill border mb-1">
                              <i className={`${getOriginIcon(doc.origin_type)} me-1`}></i> {doc.origin_type}
                            </span>
                            <br/>
                            <small className="text-muted">{doc.origin_label}</small>
                          </div>
                        </td>
                        <td>{doc.formatted_file_size}</td>
                        <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                        <td className="text-end">
                          {isPickerMode ? (
                            <button 
                              className="btn btn-sm btn-primary rounded-pill px-3"
                              onClick={() => onFileSelect && onFileSelect(doc)}
                            >
                              Select
                            </button>
                          ) : (
                            <div className="d-flex justify-content-end gap-2">
                              <button className="btn btn-sm btn-light rounded-circle" onClick={() => navigateToOrigin(doc)} title="Go to Origin">
                                <i className="bx bx-link-external"></i>
                              </button>
                              <button className="btn btn-sm btn-light rounded-circle" onClick={() => handleDownload(doc)} title="Download">
                                <i className="bx bx-download"></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <style dangerouslySetInnerHTML={{__html: `
        .doc-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
        .btn-white {
          background-color: white;
          color: #333;
        }
      `}} />
    </div>
  );

  if (isPickerMode) {
    return contentRenderer();
  }

  return (
    <div id="layout-wrapper">
      <DashboardHeader />
      <SideMenu />
      <div className="main-content" style={{ backgroundColor: '#f4f7f6', minHeight: '100vh' }}>
        <div className="page-content">
          {contentRenderer()}
        </div>
      </div>
      <DashboardFooter />
    </div>
  );
};

export default UnifiedDocumentManager;
